import React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import { useLazyListCustomerQuery } from "store/services/customerApi";

import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { BaseTabs } from "components/Shared/BaseTabs";
import BaseSelect from "components/Shared/Base/BaseSelect";
import ExportCustomerExcel from "./customerComponents/ExportCustomerExcel";

import { useAppMeta, useTablePagination } from "hooks";

const CustomerListScreen = () => {
  const [sortByDate, setSortByDate] = React.useState("createdAt:-1");

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Customer Profile" }];

  const customerListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Customer ID",
      selector: (row) => row?.customerID,
      cell: (row) => row?.customerID,
    },
    {
      name: "Name",
      selector: (row) => row.name,

      cell: (row) => (
        <Link
          title={row?.name || row?.name}
          to={`/app/crm/customerProfile/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.name ? (
            <HighlightedText text={row?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      cell: (row) => (
        <a
          href={`mailto:${row?.email}`}
          className="hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.email ? (
            <HighlightedText text={row?.email} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </a>
      ),
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      cell: (row) => {
        let phone = row?.phone || "";
        const lastDash = phone.lastIndexOf("-");
        if (lastDash !== -1) {
          phone = phone.substring(0, lastDash).trim();
        }
        return (
          <>
            {phone ? (
              <HighlightedText text={phone} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </>
        );
      },
      sortable: true,
    },
    {
      name: "Address",
      selector: (row) => row?.address,
      cell: (row) => row?.address,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/customerProfile/${row?._id}/detail`}>
            <IconButton size="small" aria-label="View Customer" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/customerProfile/${row?._id}/edit`}>
            <IconButton size="small" aria-label="Edit Customer">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [fetchCustomers, { data: customerRes, isLoading, isFetching, error }] =
    useLazyListCustomerQuery();

  React.useEffect(() => {
    const [sortedBy, sortDirection] = sortByDate.split(":");
    fetchCustomers({
      Active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
      sortedBy,
      sortDirection: parseInt(sortDirection),
    });
  }, [activeTab, fetchCustomers, currentPage, perPage, keyword, sortByDate]);
  useAppMeta(`${activeTab} Customer`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={customerListTabs}
          defaultValue={activeTab}
          actions={<ExportCustomerExcel />}
        />

        <TableFilter
          searchLabel="Name/ID/Phone/Email"
          disabled={isLoading || isFetching}
          initialFilters={2}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortByDate}
            onChange={(e) => setSortByDate(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>
        <AppTable
          columns={columns}
          data={customerRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={customerRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default CustomerListScreen;

const PageActions = () => {
  return (
    <>
      <Link to="/app/crm/customerProfile/create">
        <Button startIcon={<Add />} variant="contained">
          New Customer Profile
        </Button>
      </Link>
    </>
  );
};
